/**
 * thread/messages/index.tsx — Messages cluster.
 *
 * Contains all per-message components: RetryCard, MessageError, AssistantMessage,
 * AssistantActionBar, MessageSources, MessageTokenUsage, MessageModelLabel,
 * MessageGeneratedDocuments, UserMessage, UserActionBar, EditComposer, BranchPicker.
 *
 * Extracted from _legacy.tsx in Phase 3.
 */
"use client";

import { memo, useCallback, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionBarMorePrimitive,
  ActionBarPrimitive,
  AssistantIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  useAui,
  useAuiState,
} from "@assistant-ui/react";
import {
  BookOpenIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  FileTextIcon,
  MoreHorizontalIcon,
  PencilIcon,
  RefreshCwIcon,
  WifiOffIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { findUnstableEntry } from "@/lib/assistantUiTypes";
import { navigateToCitation } from "@/components/assistant";
import { ClientLogger } from "@/lib/clientLogger";
import { isRecoverableStreamError } from "@/lib/chatStreamError";
import { useLinkPreview } from "@/hooks/use-link-preview";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { useRetryCardState } from "@/hooks/useRetryCardState";
import { formatTokenCount, formatCost, formatDuration, type TokenUsageData } from "@/lib/tokenFormat";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { UserMessageAttachments } from "@/components/assistant-ui/attachment";
import { ProcessWindow } from "@/components/assistant-ui/process-window";
import { QuestionsTrail } from "@/components/assistant-ui/questions-trail";
import { DocumentBlock } from "@/components/assistant-ui/document-generator";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { ASSISTANT_MESSAGE_PART_COMPONENTS } from "@/components/assistant-ui/thread/constants";
import { ElicitationCard } from "@/components/custom/ElicitationCard";
import { PraetorRevisionBlock } from "@/components/document/PraetorRevisionBlock";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import { createResumeStream } from "@/components/custom/providers/threadHistoryProvider";
import { useReconnectStateStore } from "@/components/custom/stores/reconnectStateStore";
import { useDebugMode } from "@/components/custom/contexts/userSettingsContext";
import { MessageErrorBoundary } from "@/components/ui/message-error-boundary";

// Stable empty array references — prevent useMemo cache busting when unstableData
// entries haven't arrived yet (streaming start). Without these, `?? []` creates a
// new reference every frame → cascading recomputation → potential React #185.
const EMPTY_CITATIONS: readonly unknown[] = [];
const EMPTY_WEB_SOURCES: readonly unknown[] = [];
const EMPTY_IDS: readonly string[] = [];
const EMPTY_REVISIONS: readonly unknown[] = [];

// ─── RetryCard ───────────────────────────────────────────────────────────────
// Rendered on assistant messages whose backend `terminalState === "Interrupted"`
// AND that are the latest assistant message. Shows an amber card with:
// - Server-localized rate-limit message (from SSE retry-info, or generic on cold load)
// - Countdown timer (if retryAfterSec > 0)
// - Context-aware button: "Continue" (message has content) or "Retry" (empty message)

export const RetryCard: FC = () => {
  const { visible, pending, secondsLeft, actionLabel, countdownLabel, serverMessage, onAction } =
    useRetryCardState();

  if (!visible) return null;

  return (
    <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/30">
      {serverMessage && (
        <p className="mb-2 text-sm text-amber-800 dark:text-amber-200">{serverMessage}</p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5 border-amber-400 text-amber-900 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-100 dark:hover:bg-amber-900/40"
        onClick={onAction}
        disabled={pending || secondsLeft > 0}
      >
        {pending ? (
          <Spinner size="sm" />
        ) : secondsLeft > 0 ? (
          <RefreshCwIcon className="size-3.5" />
        ) : (
          <ChevronRightIcon className="size-3.5" />
        )}
        {secondsLeft > 0 ? countdownLabel : actionLabel}
      </Button>
    </div>
  );
};

export const MessageError: FC = () => {
  const { t } = useTranslation();
  const aui = useAui();
  const parentId = useAuiState(({ message }) => message.parentId);
  const streamError = useAuiState(({ message }) => message.status?.error);
  const recoverable = isRecoverableStreamError(streamError);
  const remoteId = useAuiState(state => state.threadListItem.remoteId);
  const reconnectStatus = useReconnectStateStore(state =>
    remoteId != null && state.threadId === remoteId ? state.status : "idle"
  );
  const [isReconnecting, setIsReconnecting] = useState(false);

  const handleReconnect = useCallback(() => {
    if (!remoteId || isReconnecting) return;
    setIsReconnecting(true);
    // Reset the auto-reconnect attempt counter so the manual click gets a
    // fresh 6-attempt budget; the generator also resets on entry, but doing
    // it here clears the banner instantly while resumeRun is spinning up.
    useReconnectStateStore.getState().setIdle();
    try {
      aui.thread().resumeRun({
        parentId: parentId ?? null,
        stream: createResumeStream(remoteId),
      });
    } catch (err) {
      ClientLogger.error("Reconnect failed", {
        error: err instanceof Error ? err : undefined, component: "MessageError",
      });
      setIsReconnecting(false);
    }
  }, [aui, parentId, remoteId, isReconnecting]);

  if (reconnectStatus === "connecting" || reconnectStatus === "retrying") {
    return (
      <MessagePrimitive.Error>
        <div className="aui-message-reconnecting-root mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
          <RefreshCwIcon className="size-3.5 animate-spin" />
          <span>{t("error.reconnecting")}</span>
        </div>
      </MessagePrimitive.Error>
    );
  }

  // Terminal server error (turn died on the server) — reconnecting replays
  // nothing, so offer a "Try again" re-run instead of a misleading Reconnect.
  if (!recoverable) {
    return (
      <MessagePrimitive.Error>
        <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
          <div className="flex items-center justify-between gap-2">
            <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2 flex-1" />
            <ActionBarPrimitive.Reload asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
              >
                <RefreshCwIcon className="size-3.5" />
                {t("error.tryAgain")}
              </Button>
            </ActionBarPrimitive.Reload>
          </div>
        </ErrorPrimitive.Root>
      </MessagePrimitive.Error>
    );
  }

  // Transport drop — resuming from the queue is exactly right, keep Reconnect.
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <div className="flex items-center justify-between gap-2">
          <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2 flex-1" />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
            onClick={handleReconnect}
            disabled={isReconnecting}
          >
            {isReconnecting ? (
              <RefreshCwIcon className="size-3.5 animate-spin" />
            ) : (
              <WifiOffIcon className="size-3.5" />
            )}
            {t("error.reconnect")}
          </Button>
        </div>
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

/**
 * Thin wrapper so the imports stay in this file.
 * ProcessWindow handles document-flow detection internally.
 */
const InlineProcessWindow: FC = () => <ProcessWindow />;

export const AssistantMessage: FC = memo(function AssistantMessage() {
  const messageId = useAuiState(({ message }) => message.id);
  const messageStatusType = useAuiState(({ message }) => message.status?.type);
  const messageContentSignature = useAuiState(({ message }) =>
    message.content
      .map((part, index) => {
        if (part.type === "text") return `${index}:text:${part.text.length}`;
        if (part.type === "reasoning") return `${index}:reasoning:${part.text.length}`;
        if (part.type === "tool-call") return `${index}:tool:${part.toolName}:${part.state ?? ""}`;
        if (part.type === "data") return `${index}:data:${(part as { name?: string }).name ?? ""}:${(part as { status?: string }).status ?? ""}`;
        return `${index}:${part.type}`;
      })
      .join("|")
  );
  const messagePartSignature = useAuiState(({ message }) =>
    message.parts
      .map((part, index) => {
        if (part.type === "text") return `${index}:text:${part.text.length}`;
        if (part.type === "reasoning") return `${index}:reasoning:${part.text.length}`;
        if (part.type === "tool-call") return `${index}:tool:${part.toolName}:${part.status?.type ?? ""}`;
        return `${index}:${part.type}`;
      })
      .join("|")
  );
  const metadataTypes = useAuiState(({ message }) =>
    (message.metadata?.unstable_data ?? [])
      .map((entry: unknown) =>
        entry != null && typeof entry === "object" && "type" in entry
          ? String((entry as { type?: unknown }).type)
          : typeof entry
      )
      .join("|")
  );
  const isLive = useAuiState((s) => {
    const msgs = s.thread.messages;
    const isLast = msgs.length > 0 && msgs[msgs.length - 1]?.id === s.message.id;
    return isLast && s.thread.isRunning;
  });

  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in py-3 duration-150"
      data-role="assistant"
    >
      <MessageErrorBoundary
        messageId={messageId}
        diagnostics={{
          messageStatusType,
          isLive,
          contentSignature: messageContentSignature,
          partSignature: messagePartSignature,
          metadataTypes,
        }}
      >
        <div className="aui-assistant-message-content wrap-break-word px-2 font-body text-foreground leading-relaxed">
          <InlineProcessWindow />
          <QuestionsTrail />
          <MessagePrimitive.Parts components={ASSISTANT_MESSAGE_PART_COMPONENTS} />
          <MessageGeneratedDocuments />
          <MessagePraetorRevisions />
          {isLive && <MessageMcpElicitations />}
          <MessageError />
          {isLive && <RetryCard />}
        </div>
        <div className="aui-assistant-message-footer mt-1 ml-2 flex items-center">
          <AssistantActionBar />
          <BranchPicker className="aui-assistant-branch-picker ml-1" />
          <div className="ml-auto flex items-center gap-2">
            <MessageSources />
            <MessageTokenUsage />
            <MessageModelLabel />
          </div>
        </div>
      </MessageErrorBoundary>
    </MessagePrimitive.Root>
  );
});

const MessageModelLabel: FC = () => {
  const modelId = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "model")?.modelId ?? null
  );

  if (!modelId) return null;

  return (
    <span className="select-none font-mono text-[11px] text-muted-foreground/60">
      {modelId}
    </span>
  );
};

/**
 * Inline footer cards for any documents the message produced. Rendered between the
 * message body parts and the action bar so the cards visually belong to *this*
 * message rather than the thread chrome. Driven by the `generated-documents` entry
 * in `message.metadata.unstable_data`, which is populated by both the streaming
 * adapter (live turns) and the thread-history hydrator (cold loads).
 */
/**
 * Inline approval cards for praetor-mcp scope-filter elicitations. One card per
 * `mcp-elicitation` entry on this message's `unstable_data`. The streaming
 * adapter parks entries while they're outstanding and clears them implicitly on
 * the next emit() once the user response unblocks the server-side TCS and the
 * agent loop continues — the card's own `submitted` latch hides it the moment
 * the POST returns 204 so there's no flash of "approved but still showing".
 */
const MessageMcpElicitations: FC = memo(function MessageMcpElicitations() {
  const unstableData = useAuiState(({ message }) => message.metadata?.unstable_data);
  const messageStatus = useAuiState(({ message }) => message.status);

  const entries = useMemo(() => {
    if (!Array.isArray(unstableData)) return [];
    // A completed/interrupted message can never have a live elicitation — the turn
    // is over and the dispatcher TCS has already resolved. Suppress stale cards that
    // survive in the queue replay / unstable_data from the streaming adapter.
    if (messageStatus?.type === "complete" || messageStatus?.type === "incomplete") return [];
    return unstableData.filter(
      (d: unknown): d is { type: "mcp-elicitation"; id: string; message: string; schema: unknown; context: unknown } =>
        d != null && typeof d === "object" && (d as { type?: string }).type === "mcp-elicitation",
    );
  }, [unstableData, messageStatus]);

  if (entries.length === 0) return null;
  return (
    <div className="mt-2 space-y-2">
      {entries.map((e) => (
        <ElicitationCard
          key={e.id}
          id={e.id}
          message={e.message}
          schema={e.schema}
          context={e.context}
        />
      ))}
    </div>
  );
});

const MessageGeneratedDocuments: FC = memo(function MessageGeneratedDocuments() {
  const docIds = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "generated-documents")?.ids ?? EMPTY_IDS
  );
  // Activate the `(isUpdate && updatingVersion)` gate inside DocumentBlock so the
  // "generating new version" skeleton shows during a vN+1 regeneration instead of
  // the stale completed view (Bug C — prop existed but no caller forwarded it).
  const entries = useGeneratedDocumentStore((s) => s.entries);

  if (docIds.length === 0) return null;
  return (
    <div className="mt-2 space-y-2">
      {docIds.map((id) => (
        <DocumentBlock key={id} docId={id} isUpdate={entries[id]?.updatingVersion === true} />
      ))}
    </div>
  );
});

/** Inline cards for Praetor case-document revisions made via save_revision in this turn. */
const MessagePraetorRevisions: FC = memo(function MessagePraetorRevisions() {
  const revisions = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "praetor-revisions")?.revisions ?? EMPTY_REVISIONS
  ) as readonly { id: string; title?: string }[];

  if (revisions.length === 0) return null;
  return (
    <div className="mt-2 space-y-2">
      {revisions.map((r) => (
        <PraetorRevisionBlock key={r.id} praetorDocumentId={r.id} title={r.title} />
      ))}
    </div>
  );
});

const MessageTokenUsage: FC = memo(function MessageTokenUsage() {
  const { debugMode: isDebug } = useDebugMode();
  const { t } = useTranslation();

  const usage = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "usage") ?? null
  ) as TokenUsageData | null;

  if (!usage?.totalTokens) return null;
  const cost = usage.estimatedCostUsd ?? null;
  const costDisplay = usage.estimatedCostDisplay ?? null;
  const duration = usage.totalDurationMs ?? null;

  // Debug mode: show token breakdown
  // Normal mode: show cost estimate (or total tokens if no pricing available)
  const inlineLabel = isDebug
    ? [
        usage.inputTokens ? `\u2193${formatTokenCount(usage.inputTokens)}` : null,
        usage.cachedTokens ? `\u229A${formatTokenCount(usage.cachedTokens)}` : null,
        usage.outputTokens ? `\u2191${formatTokenCount(usage.outputTokens)}` : null,
        usage.reasoningTokens ? `\uD83E\uDDE0${formatTokenCount(usage.reasoningTokens)}` : null,
      ].filter(Boolean).join(" ")
    : costDisplay
      ? costDisplay
      : cost != null
        ? `~${formatCost(cost)}`
        : t("thread.tokens", { count: formatTokenCount(usage.totalTokens) });

  const tooltipLines = [
    t("thread.inputTokens", { count: (usage.inputTokens ?? 0).toLocaleString() }),
    ...(usage.cachedTokens ? [t("thread.cachedTokens", { count: usage.cachedTokens.toLocaleString() })] : []),
    ...(usage.cacheCreationTokens ? [`Cache write: ${usage.cacheCreationTokens.toLocaleString()}`] : []),
    t("thread.outputTokens", { count: (usage.outputTokens ?? 0).toLocaleString() }),
    ...(usage.reasoningTokens ? [t("thread.reasoningTokens", { count: usage.reasoningTokens.toLocaleString() })] : []),
    t("thread.totalTokens", { count: (usage.totalTokens ?? 0).toLocaleString() }),
    ...(usage.webSearchRequests ? [`Web searches: ${usage.webSearchRequests}`] : []),
    ...(costDisplay ? [costDisplay] : cost != null ? [t("thread.estCost", { cost: formatCost(cost) })] : []),
    ...(duration != null ? [`⏱ ${formatDuration(duration)}`] : []),
  ];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="font-mono text-[11px] select-none cursor-default text-muted-foreground/40">
          {inlineLabel}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {tooltipLines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </TooltipContent>
    </Tooltip>
  );
});

// ─── Message Sources Footer ─────────────────────────────────────────────────

type SourceEntry = {
  iconUrl: string;
  iconAlt: string;
  label: string;
  href: string;
  type: "praetor" | "aspi" | "websearch" | "external";
  snippet?: string;
  pages?: string;
  docId?: string;
  number?: number;
};

const SOURCE_PRIORITY = { praetor: 0, aspi: 1, websearch: 2, external: 3 } as const;

function SourceCopyButton({ url }: { url: string }) {
  const { isCopied, copyToClipboard } = useCopyToClipboard(2000);
  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      copyToClipboard(url);
    },
    [url, copyToClipboard],
  );
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 p-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
    >
      {isCopied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
    </button>
  );
}

function SourceEntryRow({ source, onClick }: { source: SourceEntry; onClick: () => void }) {
  const { t } = useTranslation();
  const preview = useLinkPreview(source.type !== "praetor" ? source.href : undefined);
  const title = source.type !== "praetor" ? (preview.title || source.label) : source.label;

  return (
    <div className="flex items-start transition-colors hover:bg-muted/60">
      <PopoverPrimitive.Close asChild>
        <button
          type="button"
          className="flex flex-1 items-start gap-2 px-3 py-2 text-left cursor-pointer min-w-0"
          onClick={onClick}
        >
          <img
            src={source.iconUrl}
            alt={source.iconAlt}
            className="size-3.5 rounded-full shrink-0 mt-0.5"
          />
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-xs font-medium text-foreground line-clamp-1">
              {source.type === "websearch" && source.number != null ? (
                <span className="font-mono">[{source.number}] </span>
              ) : ""}{title}
            </span>
            {source.type === "praetor" && source.snippet && (
              <span className="text-xs text-foreground/80 line-clamp-2 leading-snug">
                &ldquo;{source.snippet}&rdquo;
              </span>
            )}
            {source.type === "websearch" && source.snippet && (
              <span className="text-xs text-foreground/80 line-clamp-2 leading-snug">
                &ldquo;{source.snippet}&rdquo;
              </span>
            )}
            {source.type !== "praetor" && source.type !== "websearch" && preview.description ? (
              <span className="text-xs text-foreground/70 line-clamp-2 leading-snug">
                {preview.description}
              </span>
            ) : source.type !== "praetor" && preview.loaded ? (
              <span className="text-[10px] text-muted-foreground italic">
                {t("links.missingPreview")}
              </span>
            ) : null}
          </div>
        </button>
      </PopoverPrimitive.Close>
      <SourceCopyButton url={source.type === "praetor" ? source.label : source.href} />
    </div>
  );
}

const MessageSources: FC = memo(function MessageSources() {
  const { t } = useTranslation();
  const routeContext = useRouteContext();
  const markdownText = useAuiState(({ message }) =>
    message.content
      .filter((p): p is { type: "text"; text: string } => p.type === "text")
      .map((p) => p.text)
      .join("\n")
  );
  const allCitations = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "citations")?.citations ?? EMPTY_CITATIONS
  );
  const allWebSources = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "web-sources")?.sources ?? EMPTY_WEB_SOURCES
  );

  const sources: SourceEntry[] = useMemo(() => {
    const seen = new Set<string>();
    const result: SourceEntry[] = [];

    // 1. Praetor citations from metadata (persisted)
    for (const c of allCitations) {
      const key = `praetor:${c.docId}:${c.pages ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({
        iconUrl: "/praetor-avatar.png",
        iconAlt: "Praetor",
        label: c.name ?? c.docId,
        href: `cite:${c.docId}`,
        type: "praetor",
        snippet: c.snippet,
        pages: c.pages,
        docId: c.docId,
      });
    }

    // 2. Web-search citations from metadata. Deduped by URL — multiple citation
    // sites pointing at the same page collapse into one footer row, with the
    // first-seen snippet as the preview. Numbering matches the inline `[N]`
    // markers (URL-discovery order).
    let webNumber = 0;
    const webSeen = new Set<string>();
    for (const w of allWebSources) {
      if (!w.url || webSeen.has(w.url)) continue;
      webSeen.add(w.url);
      webNumber += 1;
      const key = `websearch:${w.url}`;
      if (seen.has(key)) continue;
      seen.add(key);
      // Also seed the raw URL so the markdown-link extractor below doesn't
      // add a duplicate row for the same URL when Claude links to it inline.
      seen.add(w.url);
      let host = w.url;
      try { host = new URL(w.url).hostname; } catch { /* fall back to raw URL */ }
      result.push({
        iconUrl: `https://www.google.com/s2/favicons?domain=${host}&sz=32`,
        iconAlt: host,
        label: w.title || host,
        href: w.url,
        type: "websearch",
        snippet: w.citedText ?? undefined,
        number: webNumber,
      });
    }

    // 3. Extract ASPI + external links from markdown text
    // Match both [text](url) markdown links and bare URLs
    const mdLinkRegex = /\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g;
    const bareLinkRegex = /(?<!\()(https?:\/\/[^\s)\]]+)/g;

    const addLink = (href: string, label?: string) => {
      if (seen.has(href)) return;
      seen.add(href);
      try {
        const hostname = new URL(href).hostname;
        if (hostname.startsWith("www.aspi.")) {
          result.push({
            iconUrl: "/aspi-avatar.png", iconAlt: "ASPI",
            label: label || hostname, href, type: "aspi",
          });
        } else {
          result.push({
            iconUrl: `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`,
            iconAlt: hostname, label: label || hostname, href, type: "external",
          });
        }
      } catch { /* skip malformed URLs */ }
    };

    for (const match of markdownText.matchAll(mdLinkRegex)) {
      addLink(match[2], match[1]);
    }
    for (const match of markdownText.matchAll(bareLinkRegex)) {
      addLink(match[1]);
    }

    return result.sort((a, b) => SOURCE_PRIORITY[a.type] - SOURCE_PRIORITY[b.type]);
  }, [markdownText, allCitations, allWebSources]);

  const handleSourceClick = useCallback((source: SourceEntry) => {
    if (source.type === "praetor" && source.docId) {
      navigateToCitation({ docId: source.docId, pages: source.pages, snippet: source.snippet }, allCitations, routeContext.threadId);
    } else {
      window.open(source.href, "_blank");
    }
  }, [allCitations, routeContext.threadId]);

  // Deduplicate icons for the trigger (one per source type / external domain)
  const uniqueIcons = useMemo(() => {
    const seen = new Set<string>();
    const icons: { url: string; alt: string }[] = [];
    for (const s of sources) {
      const key = s.type === "external" ? s.iconUrl : s.type;
      if (seen.has(key)) continue;
      seen.add(key);
      icons.push({ url: s.iconUrl, alt: s.iconAlt });
    }
    return icons;
  }, [sources]);

  if (sources.length === 0) return null;

  const MAX_ICONS = 5;
  const visibleIcons = uniqueIcons.slice(0, MAX_ICONS);
  const hiddenIconCount = uniqueIcons.length - MAX_ICONS;

  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-lg px-2 py-1 hover:bg-muted/60 transition-colors cursor-pointer"
        >
          <span className="text-[11px] font-medium text-muted-foreground">
            {t("reasoning.sources")}
          </span>
          <div className="flex items-center">
            {visibleIcons.map((icon, i) => (
              <img
                key={`${icon.alt}-${i}`}
                src={icon.url}
                alt={icon.alt}
                className={cn(
                  "size-4 rounded-full shrink-0",
                  i > 0 && "-ml-1 ring-1.5 ring-background",
                )}
              />
            ))}
          </div>
          <span className="text-[10px] text-muted-foreground">
            {sources.length}{hiddenIconCount > 0 ? ` (+${hiddenIconCount})` : ""}
          </span>
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="top"
          align="start"
          sideOffset={4}
          className="z-50 w-72 max-h-80 overflow-y-auto rounded-xl border border-border bg-popover shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          <div className="px-3 py-2 border-b border-border/50">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              {t("reasoning.sources")} ({sources.length})
            </span>
          </div>
          <div className="py-1">
            {sources.map((source, i) => (
              <SourceEntryRow key={`${source.type}-${i}`} source={source} onClick={() => handleSourceClick(source)} />
            ))}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});

const AssistantActionBar: FC = () => {
  const { t } = useTranslation();
  const routeContext = useRouteContext();
  const aui = useAui();

  const citations = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "citations")?.citations ?? EMPTY_CITATIONS
  );

  const handleOpenCitations = useCallback(() => {
    if (!citations?.length) return;
    for (const citation of citations) {
      navigateToCitation(citation, citations, routeContext.threadId);
    }
  }, [citations, routeContext.threadId]);

  const handleExportConversation = useCallback(() => {
    const messages = aui.thread().getState().messages;
    if (!messages.length) return;
    const lines: string[] = ["# Conversation\n"];
    for (const msg of messages) {
      const role = msg.role === "user" ? "User" : msg.role === "assistant" ? "Assistant" : "System";
      lines.push(`## ${role}\n`);
      for (const part of msg.content) {
        if (part.type === "text" && part.text) {
          lines.push(part.text + "\n");
        }
      }
      lines.push("");
    }
    const content = lines.join("\n");
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversation-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [aui]);

  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="never"
      autohideFloat="single-branch"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground data-floating:absolute data-floating:rounded-md data-floating:border data-floating:bg-background data-floating:p-1 data-floating:shadow-sm"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip={t("assistant.copy")}>
          <AssistantIf condition={({ message }) => message.isCopied}>
            <CheckIcon />
          </AssistantIf>
          <AssistantIf condition={({ message }) => !message.isCopied}>
            <CopyIcon />
          </AssistantIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip={t("assistant.reload")}>
          <RefreshCwIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
      {citations.length > 0 && (
        <TooltipIconButton
          tooltip={t("assistant.openCitations", { count: String(citations.length) })}
          onClick={handleOpenCitations}
        >
          <BookOpenIcon />
        </TooltipIconButton>
      )}
      <ActionBarMorePrimitive.Root>
        <ActionBarMorePrimitive.Trigger asChild>
          <TooltipIconButton
            tooltip={t("assistant.more")}
            className="data-[state=open]:bg-accent"
          >
            <MoreHorizontalIcon />
          </TooltipIconButton>
        </ActionBarMorePrimitive.Trigger>
        <ActionBarMorePrimitive.Content
          side="bottom"
          align="start"
          className="aui-action-bar-more-content z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <ActionBarPrimitive.ExportMarkdown asChild>
            <ActionBarMorePrimitive.Item className="aui-action-bar-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
              <DownloadIcon className="size-4" />
              {t("assistant.exportMarkdown")}
            </ActionBarMorePrimitive.Item>
          </ActionBarPrimitive.ExportMarkdown>
          <ActionBarMorePrimitive.Item
            className="aui-action-bar-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
            onClick={handleExportConversation}
          >
            <FileTextIcon className="size-4" />
            {t("assistant.exportConversation")}
          </ActionBarMorePrimitive.Item>
        </ActionBarMorePrimitive.Content>
      </ActionBarMorePrimitive.Root>
    </ActionBarPrimitive.Root>
  );
};

export const UserMessage: FC = memo(function UserMessage() {
  return (
    <MessagePrimitive.Root
      className="group/user-msg aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto grid w-full max-w-(--thread-max-width) animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 duration-150 [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <UserMessageAttachments />

      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div className="aui-user-message-content wrap-break-word rounded-2xl bg-muted px-4 py-2.5 font-body text-foreground">
          <div className="flex justify-center">
            <MessagePrimitive.Parts />
          </div>
        </div>
        <div className="aui-user-action-bar-wrapper absolute top-1/2 left-0 -translate-x-full -translate-y-1/2 pr-2 opacity-0 pointer-events-none group-hover/user-msg:opacity-100 group-hover/user-msg:pointer-events-auto transition-opacity">
          <UserActionBar />
        </div>
      </div>

      <BranchPicker className="aui-user-branch-picker col-span-full col-start-1 row-start-3 -mr-1 justify-end" />
    </MessagePrimitive.Root>
  );
});

const UserActionBar: FC = () => {
  const { t } = useTranslation();
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip={t("assistant.edit")} className="aui-user-action-edit p-4">
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

export const EditComposer: FC = () => {
  const { t } = useTranslation();

  return (
    <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
      <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 font-body text-foreground text-sm outline-none"
          autoFocus
        />
        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm">
              {t("assistant.cancel")}
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm">{t("assistant.update")}</Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

export const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  const { t } = useTranslation();
  const branchCount = useAuiState((s) => s.message.branchCount);
  const isSingle = (branchCount ?? 1) < 2;
  return (
    <BranchPickerPrimitive.Root
      className={cn(
        "aui-branch-picker-root mr-2 -ml-2 inline-flex items-center text-muted-foreground text-xs",
        isSingle && "invisible pointer-events-none",
        className,
      )}
      aria-hidden={isSingle}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip={t("assistant.previous")}>
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip={t("assistant.next")}>
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
